export type EntranceLifecycleState = 'active' | 'draining' | 'emergency_blocked' | 'deleted';
export type EntranceLifecycleAction = 'enable' | 'disable' | 'emergency_block' | 'delete';
export interface EntranceLifecycle {
  entranceId: string;
  revision: number;
  state: EntranceLifecycleState;
  emergencyEpoch: number;
  dependencies: { bindingId: string; appId: string; revision: number }[];
  registryUncertain: boolean;
  impactToken: string;
  activeAdmissions: number;
  drainDeadline: string | null;
  canEnable?: boolean;
  enableBlockedReason?: string;
}
const enableReasons: Record<string, string> = {
  deleted: '入口已删除，不能重新启用。',
  trust_unavailable: 'AppBoss 连接未启用或凭据尚未配齐，请先检查共享连接。',
  upstream_unavailable: '此入口的上游不可用，请先检查上游服务。',
  upstream_credential_missing: '此上游需要访问凭据，但尚未配置。请在“上游服务”中检查此上游的凭据，不要更换 AppBoss 连接凭据。',
  routing_unsupported: '此入口的路由配置暂不支持 AppBoss 付费调用。',
  adapter_unqualified: '此服务尚未支持 AppBoss 令牌调用。这是功能未接通，不是连接设置错误。现有 API Key 方式不受影响。',
  paid_access_disabled: 'AppBoss 付费调用尚未开放，暂不能启用。',
  translation_access_disabled: '服务器尚未开放 AppBoss 令牌翻译，请联系服务管理员开启此能力。现有入口不受影响。',
  translation_discovery_unavailable: '暂时无法读取翻译服务的模型列表，请检查上游服务后重试。',
  translation_model_routing_pending: '所选翻译模型的路由暂未接通，请检查此入口的模型和路由配置。',
  readiness_unavailable: '服务器暂时无法确认启用条件，请稍后刷新。'
};
export function lifecycleEnableBlockedReason(view: Pick<EntranceLifecycle, 'canEnable' | 'enableBlockedReason'>) {
  if (view.canEnable !== false) return '';
  return view.enableBlockedReason && Object.prototype.hasOwnProperty.call(enableReasons, view.enableBlockedReason)
    ? enableReasons[view.enableBlockedReason] : enableReasons.readiness_unavailable;
}
const states: EntranceLifecycleState[] = ['active', 'draining', 'emergency_blocked', 'deleted'];
const actions: EntranceLifecycleAction[] = ['enable', 'disable', 'emergency_block', 'delete'];
const safeID = (value: unknown): value is string => typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9_.:@/-]{0,199}$/.test(value);
const count = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;

export function publicEntranceLifecycle(value: unknown): EntranceLifecycle {
  const item = value as EntranceLifecycle | null;
  if (!item || !safeID(item.entranceId) || !count(item.revision) || item.revision < 1 || item.revision >= Number.MAX_SAFE_INTEGER ||
      !states.includes(item.state) || !count(item.emergencyEpoch) || !count(item.activeAdmissions) ||
      typeof item.registryUncertain !== 'boolean' || !/^[a-f0-9]{64}$/.test(item.impactToken) ||
      item.canEnable !== undefined && typeof item.canEnable !== 'boolean' ||
      item.enableBlockedReason !== undefined && typeof item.enableBlockedReason !== 'string' ||
      !Array.isArray(item.dependencies) || item.dependencies.length > 4096 ||
      item.dependencies.some((dep) => !dep || !safeID(dep.bindingId) || !safeID(dep.appId) || !count(dep.revision) || dep.revision < 1) ||
      !(item.drainDeadline === null || typeof item.drainDeadline === 'string' && Number.isFinite(Date.parse(item.drainDeadline)))) {
    throw new Error('Invalid lifecycle response');
  }
  return {
    entranceId: item.entranceId, revision: item.revision, state: item.state,
    emergencyEpoch: item.emergencyEpoch, activeAdmissions: item.activeAdmissions,
    registryUncertain: item.registryUncertain, impactToken: item.impactToken, drainDeadline: item.drainDeadline,
    dependencies: item.dependencies.map(({ bindingId, appId, revision }) => ({ bindingId, appId, revision })),
    ...(item.canEnable !== undefined ? { canEnable: item.canEnable } : {}),
    ...(item.canEnable === false ? { enableBlockedReason: item.enableBlockedReason && Object.prototype.hasOwnProperty.call(enableReasons, item.enableBlockedReason) ? item.enableBlockedReason : 'readiness_unavailable' } : {})
  };
}
export function lifecycleStateLabel(state: EntranceLifecycleState) {
  return { active: '接受新请求', draining: '已停止新请求', emergency_blocked: '紧急阻断', deleted: '已删除（保留历史）' }[state];
}
export function lifecycleActionLabel(action: EntranceLifecycleAction) {
  return { enable: '启用入口', disable: '停止新请求', emergency_block: '紧急阻断', delete: '删除入口' }[action];
}
export function lifecyclePayload(view: EntranceLifecycle, action: EntranceLifecycleAction, reason: string, force: boolean, acknowledged: boolean) {
  const snapshot = publicEntranceLifecycle(view);
  if (typeof reason !== 'string' || Array.from(reason).length > 500 || Array.from(reason).some((character) => /^[\uD800-\uDFFF]$/.test(character))) throw new Error('Invalid lifecycle note');
  const text = reason.trim();
  if (!actions.includes(action) || snapshot.state === 'deleted' || action === 'enable' && snapshot.canEnable !== true || !acknowledged ||
      force && action !== 'delete' || action === 'delete' && !force && snapshot.dependencies.length > 0) throw new Error('Invalid lifecycle confirmation');
  return { action, expectedRevision: snapshot.revision, ...(text ? { reason: text } : {}), force, impactToken: force ? snapshot.impactToken : '' };
}
